import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormRow } from '@tanium/react-form';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import { Button } from '@tanium/react-button';
import { SmallSubtleText } from '../../TextStyles';
import { LoginFormTextInput, LoginFormButton } from './LoginFormComponents';
import { useAsync, AsyncStatus } from '../../hooks/useAsync';
import { TaniumLoginCard } from './TaniumLoginCard';
import { ErrorText } from './ErrorText';
import { useOnInputChange } from '../../components/InputHandlerUtils';
import { AuthCallbacks } from '../../auth/Auth';
import { UnauthenticatedRoutes } from '../../components/UnauthenticatedArea/UnauthenticatedRoutes';
import { SessionKeys } from '../../types';
import { ApiError, parseApiErrorMsg } from '../../hooks/useApiErrorMsgParser';

interface WorkspaceSelectionProps {
  auth: AuthCallbacks;
}

export const WorkspaceHelpPage: React.VFC<WorkspaceSelectionProps> = ({ auth }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');

  const { status, error, execute } = useAsync(() => {
    if (username === '') {
      throw new Error(t('Username is required'));
    }

    const email = sessionStorage.getItem(SessionKeys.Email) || '';

    return auth.signInIdpHelper(username, email).catch((err: ApiError) => {
      if (err.response?.status === 404) {
        throw new Error(t('Account owner not found'));
      }
      throw new Error(parseApiErrorMsg(t, err));
    });
  });

  const handleUsernameChange = useOnInputChange((value: string) => setUsername(value.toLowerCase()));

  const onSubmit = useCallback<React.FormEventHandler>(
    async (event) => {
      event.preventDefault();
      await execute();
    },
    [execute]
  );

  const onCancel = () => {
    sessionStorage.setItem(SessionKeys.Email, '');
    window.location.hash = UnauthenticatedRoutes.LOGIN_ROUTE;
  };

  return (
    <Box as={Stack} justifyContent="center" marginTop="150px">
      <TaniumLoginCard>
        <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box style={{ flexGrow: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
            <form style={{ width: '100%' }} onSubmit={onSubmit}>
              <FormRow label={t('Account Admin Username')} dataKey="username">
                <LoginFormTextInput
                  value={username}
                  placeholder={'user@company.mytanium.com'}
                  onChange={handleUsernameChange}
                  required
                />
                {error && <ErrorText>{error.message}</ErrorText>}
              </FormRow>
              <Stack itemSpacing="2" justifyContent="left">
                <LoginFormButton loading={status === AsyncStatus.Pending}>{t('Continue')}</LoginFormButton>
                <Button variant="secondary" onClick={onCancel}>
                  {t('Cancel')}
                </Button>
              </Stack>
            </form>
          </Box>
          <Stack justifyContent="center" style={{ margin: 'auto 0 10px 0' }}>
            <SmallSubtleText>{t("Still having trouble? Reach out to your Account's Admin for help.")}</SmallSubtleText>
          </Stack>
        </Box>
      </TaniumLoginCard>
    </Box>
  );
};
